import { useRef, useState, type ComponentType } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Check, Copy } from 'lucide-react'

/**
 * Detects if a URL is an internal app route that should use client-side navigation.
 */
function isInternalLink(url: string): boolean {
  if (!url) return false
  // Relative paths starting with / are internal routes
  if (url.startsWith('/')) return true
  // Same-origin absolute URLs
  try {
    const parsed = new URL(url)
    return parsed.origin === window.location.origin
  } catch {
    return false
  }
}

/**
 * Extract the pathname from a URL (handles both absolute and relative).
 */
function toPathname(url: string): string {
  if (url.startsWith('/')) return url
  try {
    return new URL(url).pathname + new URL(url).search + new URL(url).hash
  } catch {
    return url
  }
}

/**
 * Custom link component for Streamdown that handles internal app links
 * via client-side navigation instead of full page reload.
 */
const ChatLink: ComponentType<JSX.IntrinsicElements['a']> = ({ href, children, ...props }) => {
  const navigate = useNavigate()

  if (href && isInternalLink(href)) {
    return (
      <a
        {...props}
        href={href}
        onClick={(e) => {
          e.preventDefault()
          navigate({ to: toPathname(href) })
        }}
        className="text-cobalt-strong underline decoration-cobalt-solid/30 hover:decoration-cobalt-solid transition-colors cursor-pointer"
      >
        {children}
      </a>
    )
  }

  // External links open in new tab
  return (
    <a
      {...props}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-cobalt-strong underline decoration-cobalt-solid/30 hover:decoration-cobalt-solid transition-colors"
    >
      {children}
    </a>
  )
}

/**
 * Blockquote with a copy button. Vera often returns ready-to-paste drafts
 * (e.g. a reply to an auditor) as a markdown quote; the button makes it a
 * one-click copy. Keeps Streamdown's default blockquote styling so the look
 * is unchanged.
 */
const ChatBlockquote = ({
  children,
  className,
  // react-markdown passes `node`; keep it off the DOM element.
  node: _node,
  ...props
}: JSX.IntrinsicElements['blockquote'] & { node?: unknown }) => {
  const quoteRef = useRef<HTMLQuoteElement>(null)
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    const text = quoteRef.current?.innerText?.trim()
    if (!text) return
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      /* clipboard unavailable — no-op */
    }
  }

  return (
    <div className="group/quote relative my-4">
      <blockquote
        ref={quoteRef}
        {...props}
        className={
          'border-muted-foreground/30 border-l-4 pr-9 pl-4 text-muted-foreground italic' +
          (className ? ` ${className}` : '')
        }
      >
        {children}
      </blockquote>
      <button
        type="button"
        onClick={handleCopy}
        aria-label={copied ? 'Copied' : 'Copy quoted text'}
        title={copied ? 'Copied' : 'Copy'}
        className="absolute right-1.5 top-1.5 inline-flex h-6 w-6 items-center justify-center rounded-sm border border-border bg-card text-text-subtle opacity-70 transition-opacity hover:text-text-secondary group-hover/quote:opacity-100"
      >
        {copied ? (
          <Check className="h-3.5 w-3.5 text-cobalt-strong" />
        ) : (
          <Copy className="h-3.5 w-3.5" />
        )}
      </button>
    </div>
  )
}

/**
 * Shared Streamdown components config for chat messages.
 * Pass this to `<Streamdown components={chatStreamdownComponents}>`.
 */
export const chatStreamdownComponents = {
  a: ChatLink,
  blockquote: ChatBlockquote,
}
